import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from './cn';

/*
  HoldToConfirm — предохранитель на необратимом действии (DESIGN.md R27).
  Приём характера: опасное действие не прячется за модалкой «вы уверены?», которую
  прокликивают не глядя. Кнопку нужно подержать: под текстом наливается полоса,
  отпустил раньше — откатилась. Решение принимается пальцем, а не ещё одним диалогом.
  Клавиатура работает так же: удержание пробела или Enter на сфокусированной кнопке.

  Наполнение — один CSS-переход длиной holdMs (а не покадровая петля): оно не зависит
  от того, рисует ли вкладка кадры, и не держит поток во время удержания.
*/

interface HoldToConfirmProps {
  children: ReactNode;
  onConfirm: () => void;
  /** Сколько держать. */
  holdMs?: number;
  /** Подпись-подсказка справа от текста. */
  hint?: string;
  disabled?: boolean;
  /** sm — для строк таблиц рядом с кнопками sm (одна высота в строке, R11). */
  size?: 'sm' | 'md';
  className?: string;
}

export function HoldToConfirm({
  children,
  onConfirm,
  holdMs = 900,
  hint = 'удерживайте',
  disabled,
  size = 'md',
  className,
}: HoldToConfirmProps) {
  const [holding, setHolding] = useState(false);
  const timer = useRef(0);

  const stop = useCallback(() => {
    window.clearTimeout(timer.current);
    setHolding(false);
  }, []);

  const start = useCallback(() => {
    if (disabled || holding) return;
    setHolding(true);
    timer.current = window.setTimeout(() => {
      setHolding(false);
      onConfirm();
    }, holdMs);
  }, [disabled, holding, holdMs, onConfirm]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={typeof children === 'string' ? `${children}: ${hint}` : undefined}
      onPointerDown={start}
      onPointerUp={stop}
      onPointerLeave={stop}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          start();
        }
      }}
      onKeyUp={stop}
      onBlur={stop}
      className={cn(
        'relative isolate inline-flex items-center gap-2 overflow-hidden rounded-md font-medium whitespace-nowrap select-none',
        size === 'sm' ? 'h-8 px-3 text-xs [&_svg]:size-3.5' : 'h-9 px-4 text-sm',
        'border border-danger-line bg-danger-soft text-danger-strong',
        'transition-colors duration-fast focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        'disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
    >
      <span
        aria-hidden
        style={{
          transform: holding ? 'scaleX(1)' : 'scaleX(0)',
          transitionDuration: holding ? `${holdMs}ms` : '140ms',
        }}
        className="absolute inset-0 -z-10 origin-left bg-danger transition-transform ease-linear motion-reduce:transition-none"
      />
      <span className={cn('transition-colors duration-normal', holding && 'text-on-danger')}>{children}</span>
      <span
        className={cn(
          'text-2xs transition-colors duration-normal',
          holding ? 'text-on-danger' : 'text-fg-subtle',
        )}
      >
        {hint}
      </span>
    </button>
  );
}
